// Shared Sprint metrics utility.
// Uses the exact same definitions rendered on the Progress Dashboard, so
// TactixAI's read-only Sprint context and the dashboard cards stay in sync.

const getDayKey = (date) => new Date(date).toLocaleDateString('en-CA');
const getStartOfWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return d;
};
const getWeekKey = (date) => getDayKey(getStartOfWeek(date));

export const EMPTY_SPRINT_METRICS = {
  total_sessions: 0,
  focus_hours_logged: 0,
  completion_rate: 0,
  consistency_rate: 0,
  sessions_this_week: 0,
  current_streak: 0,
  weekly_streak: 0,
};

export const computeSprintMetrics = (sessions) => {
  if (!sessions || sessions.length === 0) return { ...EMPTY_SPRINT_METRICS };

  const total_sessions = sessions.length;

  const focus_hours_logged =
    Math.round(
      (sessions.reduce((sum, s) => sum + (s.duration_minutes || 0), 0) / 60) *
        10
    ) / 10;

  const completedCount = sessions.filter((s) => s.finished === true).length;
  const completion_rate =
    total_sessions > 0
      ? Math.round((completedCount / total_sessions) * 100)
      : 0;

  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const uniqueDays = new Set(
    sessions
      .filter((s) => new Date(s.session_date) >= thirtyDaysAgo)
      .map((s) => new Date(s.session_date).toDateString())
  );
  const consistency_rate = Math.round((uniqueDays.size / 30) * 100);

  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay());
  startOfWeek.setHours(0, 0, 0, 0);
  const sessions_this_week = sessions.filter(
    (s) => new Date(s.session_date) >= startOfWeek
  ).length;

  const daysWithSessions = new Set(
    sessions.map((s) => getDayKey(s.session_date || s.created_at))
  );
  let current_streak = 0;
  const today = getDayKey(now);
  if (daysWithSessions.has(today)) {
    let checkDate = new Date(now);
    while (daysWithSessions.has(getDayKey(checkDate))) {
      current_streak++;
      checkDate.setDate(checkDate.getDate() - 1);
    }
  }

  const weeksWithSessions = new Set(
    sessions.map((s) => getWeekKey(s.session_date || s.created_at))
  );
  let weekly_streak = 0;
  const currentWeekKey = getWeekKey(now);
  if (weeksWithSessions.has(currentWeekKey)) {
    let checkDate = new Date(now);
    let checkWeekKey = getWeekKey(checkDate);
    while (weeksWithSessions.has(checkWeekKey)) {
      weekly_streak++;
      checkDate.setDate(checkDate.getDate() - 7);
      checkWeekKey = getWeekKey(checkDate);
    }
  }

  return {
    total_sessions,
    focus_hours_logged,
    completion_rate,
    consistency_rate,
    sessions_this_week,
    current_streak,
    weekly_streak,
  };
};

// Build a compact, token-friendly Sprint context snapshot for TactixAI.
// Only reads sessions — never mutates.
const truncate = (str, max) => {
  if (!str) return '';
  const s = String(str).replace(/\s+/g, ' ').trim();
  return s.length > max ? s.slice(0, max - 1) + '…' : s;
};

export const buildSprintContextSnapshot = (sessions) => {
  if (!Array.isArray(sessions) || sessions.length === 0) {
    return { metrics: { ...EMPTY_SPRINT_METRICS }, recent: [] };
  }
  const metrics = computeSprintMetrics(sessions);
  const sorted = [...sessions].sort((a, b) => {
    const da = new Date(a.created_at || a.session_date || 0).getTime();
    const db = new Date(b.created_at || b.session_date || 0).getTime();
    return db - da;
  });
  const recent = sorted.slice(0, 5).map((s) => {
    const tasks = Array.isArray(s.tasks) ? s.tasks : [];
    const completedTasks = tasks.filter((t) => t?.status === 'completed').length;
    // Prefer structured Win / Next Move fields on new sessions; fall back to legacy note
    const hasStructured =
      (typeof s.win_note === 'string' && s.win_note.trim()) ||
      (typeof s.next_move === 'string' && s.next_move.trim());
    const winNote = typeof s.win_note === 'string' && s.win_note.trim() ? s.win_note : null;
    const nextMove =
      typeof s.next_move === 'string' && s.next_move.trim() ? s.next_move : null;
    const legacyNote = s.proof_note || s.notes || '';
    return {
      date: (s.session_date || s.created_at || '').slice(0, 10),
      goal: truncate(s.goal, 120),
      duration_minutes: s.duration_minutes,
      status: s.finished ? 'Completed' : 'In Progress',
      mood_rating: typeof s.mood_rating === 'number' ? s.mood_rating : null,
      tasks_completed: tasks.length > 0 ? `${completedTasks} of ${tasks.length}` : null,
      win_note: winNote ? truncate(winNote, 200) : null,
      next_move: nextMove ? truncate(nextMove, 200) : null,
      note: !hasStructured && legacyNote ? truncate(legacyNote, 200) : null,
    };
  });
  return { metrics, recent };
};
